import React from 'react';

import axiosApiInstance from '@/api/shared/axios-instance';
import EstadisticasEnVivo from '@/components/page-components/partido/EstadisticasEnVivo/EstadisticasEnVivo';
import Seo from '@/components/shared/custom/seo-component';
import { LEAGUES } from '@/constants/leagues';
import { Pages, Rutas } from '@/constants/rutas';
import URL_FACTORY from '@/helpers/url-helper';
import { Box, Typography } from '@mui/material';
import PartidoInfoNavigation from '@/components/alineaciones/shared/complementos/PartidoInfoNavigation';
import { PARTIDO_TABS } from '@/components/alineaciones/shared/complementos/PartidoInfoNavigation';

const FantasyAlineacionesPage = ({ data, fixtureId }) => {
    const description = `Estadísticas fantasy de ${data?.fixture?.home?.name} contra ${data?.fixture?.away?.name}. Mejores jugadores, alineaciones, suplentes, bajas y sancionados, apercibidos y recomendaciones fantasy.`;

    const title = `${data?.fixture?.home?.name} vs ${data?.fixture?.away?.name}: Estadísticas Fantasy.`;

    const urlFixture = `${process.env.NEXT_PUBLIC_URL_FRONT}${Rutas.PARTIDO}/${fixtureId}/estadisticas-la-liga-fantasy-en-vivo`;

    const isLaLiga = data?.fixture?.leagueId === LEAGUES.LA_LIGA;

    return (
        <Box className="partido-container container">
            <Seo pageId={Pages.PARTIDO} description={description} title={title} url={urlFixture} ads={true} />

            <Typography variant="h1">{`Estadísticas fantasy del partido ${data?.fixture?.home?.name} contra ${data?.fixture?.away?.name}`}</Typography>

            <PartidoInfoNavigation
                fixture={data?.fixture}
                selectedTab={PARTIDO_TABS.ESTADISTICAS_FANTASY_EN_VIVO}
            />

            <EstadisticasEnVivo
                estadisticas={data?.estadisticas}
                fixture={data?.fixture}
                fixtureId={fixtureId}
                isLaLiga={isLaLiga}
            />
        </Box>
    );
};

export async function getStaticProps({ params }) {
    const { slug } = params;

    try {
        // Obtener todo desde el nuevo endpoint específico
        const fixtureId = parseInt(slug);
        if (isNaN(fixtureId)) {
            throw new Error(`Invalid fixture ID: ${slug}`);
        }

        const estadisticasUrl = URL_FACTORY.GetEstadisticasFantasyEnVivo(fixtureId);
        const response = await axiosApiInstance.get(estadisticasUrl);

        // Función helper para convertir undefined a null
        const sanitizeValue = (value) => (value === undefined ? null : value);

        // Función helper para sanitizar un objeto
        const sanitizeObject = (obj) => {
            if (obj === null || obj === undefined) return null;
            if (Array.isArray(obj)) {
                return obj.map(sanitizeObject).filter((item) => item !== null);
            }
            if (typeof obj === 'object') {
                const sanitized = {};
                for (const [key, value] of Object.entries(obj)) {
                    const sanitizedValue = sanitizeValue(value);
                    if (sanitizedValue !== undefined) {
                        sanitized[key] = sanitizeObject(sanitizedValue);
                    }
                }
                return sanitized;
            }
            return sanitizeValue(obj);
        };

        const homeLineup = response.data?.home?.lineup || [];
        const awayLineup = response.data?.away?.lineup || [];

        // Obtener revalidate de la respuesta (en segundos, Next.js espera segundos)
        const revalidate = response.data?.revalidate ?? 300;

        return {
            props: {
                data: sanitizeObject({
                    fixture: response.data?.fixture,
                    home: {
                        lineup: homeLineup.map((p) => ({
                            id: p.id,
                            name: p.name,
                            playerId: p.id,
                        })),
                    },
                    away: {
                        lineup: awayLineup.map((p) => ({
                            id: p.id,
                            name: p.name,
                            playerId: p.id,
                        })),
                    },
                    estadisticas: response.data?.estadisticas,
                }),
                fixtureId: slug,
            },
            revalidate: revalidate, // Revalidate dinámico desde la API
        };
    } catch (_error) {
        return {
            props: { fixtureId: slug },
            revalidate: 300,
        };
    }
}

export async function getStaticPaths() {
    // En producción, podrías generar paths estáticos aquí
    // Por ahora, retornamos paths vacío para generar bajo demanda
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export default FantasyAlineacionesPage;
